import { MEDIA_UPLOAD_URL } from '../../../cms/src/utils/apiEndpoints';
import { AUTHENTICATION_TOKEN } from '../../../cms/src/constants/common';
import { getToken } from '../../../cms/src/utils/cookie';
import { mediaUtils, config } from '../utils/media';
import serverConfig from '../../../config.json';
import { useCommands } from '@remirror/react';
import { useCallback } from 'react';
import { message } from 'antd';
import axios from 'axios';

/******************************************************************************************************
   ┌────────────────────────────────────────────────────────────────────────────────────────────────┐
   │                                    fileUploadHandler                                           │
   └────────────────────────────────────────────────────────────────────────────────────────────────┘
  *****************************************************************************************************/

export function fileUploadHandler(file, { body, attrs, mock }) {
  const formData = new FormData();
  formData.append('files', file.originFileObj);
  formData.append('file_id', body?.fileId);
  formData.append('workspace_id', body?.workspaceId);

  const suppressError = body?.suppressError || false;
  const authToken = getToken(AUTHENTICATION_TOKEN);
  const isVideo = mediaUtils.isVideo(file.originFileObj);
  const altKey = isVideo ? 'title' : 'alt';

  return async () => {
    try {
      if (mock)
        return {
          src: URL.createObjectURL(file.originFileObj),
          [altKey]: file.originFileObj?.name,
          ...attrs,
        };

      const response = await axios.post(`${serverConfig.docs.media_url}${MEDIA_UPLOAD_URL}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: authToken,
          'X-Organization': body?.organizationId,
        },
      });

      const resData = response.data;
      if (resData?.status_code === 200 && resData.data.length > 0) {
        return { src: resData.data[0]?.url, [altKey]: resData.data[0]?.file_name, ...attrs };
      }
    } catch (err) {
      if (!suppressError) message.error('Unable to upload media due to server error.');
      throw new Error('Unexpected error happened while uploading file.');
    }
  };
}

/******************************************************************************************************
   ┌────────────────────────────────────────────────────────────────────────────────────────────────┐
   │                                       uploadMedia                                              │
   └────────────────────────────────────────────────────────────────────────────────────────────────┘
  *****************************************************************************************************/

export const uploadMedia = ({
  files,
  onFinish,
  uploadImage,
  uploadVideo,
  fileId,
  workspaceId,
  mock = false,
  organizationId,
}) => {
  try {
    Array.from(files).forEach((file) => {
      const mediaType = mediaUtils.getMediaType(file);

      // throw error if our plugin does not support the provided media type
      if (!mediaUtils.supportsFormat(file))
        onFinish(new Error(`Invalid format for ${file.name}. Supported format are ${config[mediaType].allowedFormat}`));

      // throw error if file size exceeds the limit
      if (mediaUtils.exceedMaxSize(file))
        onFinish(new Error(`${file.name} exceeds max size limit: ${config[mediaType].maxSize} MB`));

      // perform uploading of file
      const rmUploadFn = mediaType === 'image' ? uploadImage : uploadVideo;
      rmUploadFn(
        fileUploadHandler(file, {
          body: { fileId, workspaceId, organizationId },
          attrs: { width: '600px' },
          mock,
        })
      );
      onFinish?.(null);
    });
  } catch (err) {
    onFinish(err.message);
  }
};

/******************************************************************************************************
   ┌────────────────────────────────────────────────────────────────────────────────────────────────┐
   │                                  useMediaServerUpload                                          │
   └────────────────────────────────────────────────────────────────────────────────────────────────┘
  *****************************************************************************************************/

export function useMediaServerUpload({ fileId, workspaceId, mock = false, organizationId }) {
  const { uploadImage, uploadVideo } = useCommands();

  const upload = useCallback(
    ({ files, onFinish }) =>
      () =>
        uploadMedia({
          files,
          onFinish,
          uploadImage,
          uploadVideo,
          fileId,
          workspaceId,
          mock,
          organizationId,
        }),
    [fileId, workspaceId, mock, uploadImage, uploadVideo, organizationId]
  );

  return upload;
}

export default useMediaServerUpload;
